import { z } from "zod";
import { dateSchema, localDate } from "../items/model";
const time = z.iso.datetime(),
  id = z.uuid();
export const contentKinds = ["resource", "decision", "diary"] as const;
export type ContentKind = (typeof contentKinds)[number];
const common = {
  id,
  createdAt: time,
  updatedAt: time,
  revision: z.number().int().positive(),
  source: z.literal("user"),
  archivedAt: time.nullable(),
  deletedAt: time.nullable(),
  title: z.string().max(200),
  body: z.string().max(200000),
  attachmentIds: z.array(id).max(30),
  tagIds: z.array(id).max(30),
};
const url = z
  .string()
  .max(4000)
  .refine(
    (v) =>
      !v ||
      (() => {
        try {
          return ["https:", "http:"].includes(new URL(v).protocol);
        } catch {
          return false;
        }
      })(),
    "链接仅支持 HTTP / HTTPS",
  );
export const resourceSchema = z
  .strictObject({
    ...common,
    title: z.string().trim().min(1).max(200),
    url,
    excerpt: z.string().max(200000),
    summary: z.string().max(200000),
  })
  .refine(
    (v) =>
      !!(
        v.body.trim() ||
        v.url ||
        v.excerpt.trim() ||
        v.summary.trim() ||
        v.attachmentIds.length
      ),
    "请保存正文、链接或图片",
  );
export const decisionSchema = z.strictObject({
  ...common,
  title: z.string().trim().min(1).max(200),
  choice: z.string().trim().min(1).max(20000),
  reason: z.string().trim().min(1).max(200000),
});
export const diarySchema = z
  .strictObject({ ...common, diaryDate: dateSchema })
  .refine(
    (v) => !!(v.body.trim() || v.attachmentIds.length),
    "请写正文或添加图片",
  );
export const contentSchemas = {
  resource: resourceSchema,
  decision: decisionSchema,
  diary: diarySchema,
};
export const contentSchema = z.union([
  resourceSchema,
  decisionSchema,
  diarySchema,
]);
export type Content = z.infer<typeof contentSchema>;
export const contentDraftSchemas = {
  resource: z.strictObject({
    ...resourceSchema.shape,
    title: common.title,
    url: z.string().max(4000),
  }),
  decision: z.strictObject({
    ...decisionSchema.shape,
    title: common.title,
    choice: z.string().max(20000),
    reason: z.string().max(200000),
  }),
  diary: z.strictObject({
    ...diarySchema.shape,
    diaryDate: z.string().max(10),
  }),
};
export const contentDraftSchema = z.strictObject({
  id,
  targetId: id,
  kind: z.enum(contentKinds),
  baseRevision: z.number().int().nonnegative(),
  sourceCaptureId: id.nullable(),
  sourceRevision: z.number().int().nonnegative(),
  revision: z.number().int().positive(),
  createdAt: time,
  updatedAt: time,
  deletedAt: time.nullable(),
  payload: z.union([
    contentDraftSchemas.resource,
    contentDraftSchemas.decision,
    contentDraftSchemas.diary,
  ]),
});
export type ContentDraft = z.infer<typeof contentDraftSchema>;
export const contentRevisionSchema = z.strictObject({
  id: z.string().max(100),
  entityType: z.enum(contentKinds),
  entityId: id,
  revision: z.number().int().positive(),
  reason: z.enum([
    "created",
    "edited",
    "trashed",
    "restored",
    "history-restored",
  ]),
  recordedAt: time,
  snapshot: contentSchema,
});
export type ContentRevision = z.infer<typeof contentRevisionSchema>;
export const tagSchema = z.strictObject({
  id,
  name: z.string().min(1).max(100),
  normalized: z.string().max(300),
});
export const contentTables = {
  resource: "resources",
  decision: "decisions",
  diary: "diaryEntries",
} as const;
export const contentLabels = {
  resource: "资料",
  decision: "决定",
  diary: "日记",
};
export const contentPath = (kind: ContentKind, id?: string) =>
  `/${contentTables[kind]}${id ? "/" + id : ""}`;
export function blankContent(kind: ContentKind, zone = "UTC"): Content {
  const now = new Date().toISOString(),
    base = {
      id: crypto.randomUUID(),
      title: "",
      body: "",
      attachmentIds: [],
      tagIds: [],
      revision: 1,
      source: "user" as const,
      createdAt: now,
      updatedAt: now,
      archivedAt: null,
      deletedAt: null,
    };
  return kind === "resource"
    ? { ...base, url: "", excerpt: "", summary: "" }
    : kind === "decision"
      ? { ...base, choice: "", reason: "" }
      : { ...base, diaryDate: localDate(new Date(), zone) };
}
export const contentTitle = (x: Content) =>
  x.title || x.body.split("\n")[0].slice(0, 80) || "无标题";
